// services/api.js - Versão completa com fetch e axios
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

console.log(`API conectando em: ${API_URL}`);

// ============================================================================
// CONFIGURAÇÃO AXIOS PARA CHAT (nova adição)
// ============================================================================

// Instância axios específica para o chat
export const chatApi = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interceptador para adicionar token automaticamente ao chat
chatApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Erro na requisição do chat:', error);
    return Promise.reject(error);
  }
);

// Interceptador para lidar com respostas de erro do chat
chatApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Erro na resposta do chat:', error.response?.data || error.message);
    
    // Se token expirou, remove e redireciona
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// ============================================================================
// SERVIÇOS DE CHAT (nova adição)
// ============================================================================

export const chatService = {
  // Verifica status do chat do usuário
  getStatus: async () => {
    const response = await chatApi.get('/api/chat/status');
    return response.data;
  },

  // Envia mensagem para Liv IAM
  sendMessage: async (message) => {
    const response = await chatApi.post('/api/chat/send', { message });
    return response.data;
  },

  // Obtém histórico da conversa
  getHistory: async () => {
    const response = await chatApi.get('/api/chat/history');
    return response.data;
  },

  // Limpa conversa
  clearChat: async () => {
    const response = await chatApi.post('/api/chat/clear');
    return response.data;
  },

  // Health check do sistema de chat
  healthCheck: async () => {
    const response = await chatApi.get('/api/chat/health');
    return response.data;
  }
};

// ============================================================================
// FUNÇÕES FETCH EXISTENTES (mantidas como estavam)
// ============================================================================

// 🔐 Login administrativo
export const loginUser = async (credentials) => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Falha no login' }));
    throw new Error(errorData.message || 'Falha no login');
  }

  return response.json();
};

// 🔐 Login do cliente
export const loginCliente = async (credentials) => {
  const response = await fetch(`${API_URL}/api/auth-client/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Erro ao autenticar cliente' }));
    throw new Error(errorData.message || 'Erro ao autenticar cliente');
  }

  return response.json();
};

// ✍️ Cadastro de administrador
export const registerUser = async (userData) => {
  const response = await fetch(`${API_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Falha no registro' }));
    throw new Error(errorData.message || 'Falha no registro');
  }

  return response.json();
};

// ✍️ Cadastro de cliente
export const registerCliente = async (clientData) => {
  const response = await fetch(`${API_URL}/api/auth-client/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clientData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Erro ao registrar cliente' }));
    throw new Error(errorData.message || 'Erro ao registrar cliente');
  }

  return response.json();
};

// 🔄 Redefinir senha do cliente
export const resetSenhaCliente = async (email) => {
  const response = await fetch(`${API_URL}/api/auth-client/reset-senha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Erro ao solicitar redefinição' }));
    throw new Error(errorData.message || 'Erro ao enviar link de redefinição');
  }

  return response.json();
};

// 🔍 Obter dados do usuário autenticado
export const fetchUserData = async (token) => {
  const response = await fetch(`${API_URL}/api/auth/profile`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Falha ao obter dados do usuário' }));
    throw new Error(errorData.message || 'Falha ao obter dados do usuário');
  }

  return response.json();
};

// 🔁 Renovar token
export const refreshToken = async (refreshToken) => {
  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) throw new Error('Falha ao renovar o token');

  return response.json();
};

// 📊 Obter token Power BI
export const getEmbedToken = async () => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_URL}/api/powerbi/embed-token`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) throw new Error('Falha ao obter token do Power BI');

  return response.json();
};

// ✉️ Requisição autenticada genérica
export const authenticatedRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Falha na requisição' }));
    throw new Error(errorData.message || 'Falha na requisição');
  }

  return response.json();
};

// ============================================================================
// OBJETOS DE SERVIÇO (atualizados)
// ============================================================================

// 📦 Objeto de serviço completo
export const authService = {
  login: loginUser,
  loginCliente: loginCliente,
  register: registerUser,
  registerCliente: registerCliente,
  resetSenhaCliente: resetSenhaCliente,
  getUserProfile: fetchUserData,
  refreshToken: refreshToken,
  getEmbedToken: getEmbedToken
};

// Separação do Power BI
export const powerBIService = {
  getEmbedToken: getEmbedToken
};

// ============================================================================
// EXPORTS DEFAULT E COMPATIBILIDADE
// ============================================================================

// Compatibilidade com import default
export default {
  // Funções originais
  loginUser,
  loginCliente,
  registerUser,
  registerCliente,
  resetSenhaCliente,
  fetchUserData,
  refreshToken,
  getEmbedToken,
  authenticatedRequest,
  
  // Serviços organizados
  authService,
  powerBIService,
  chatService, // Novo serviço de chat
  
  // Instância axios para chat
  chatApi
};